import { useCallback, useEffect, useState } from "react";
import { PhoneIncoming, PhoneOutgoing, Play, Sparkles, RefreshCw, Languages } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Aircall call history panel — shows inbound/outbound calls for a prospect
 * with direction, duration, AI summary (auto-translated to the UI language)
 * and a recording link when available.
 */
export default function AircallCallHistory({ api, prospectId }) {
  const { t, i18n } = useTranslation();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!prospectId) return;
    setLoading(true);
    try {
      const r = await api.get(`/aircall/prospect/${prospectId}/calls?lang=${i18n.language}`);
      setCalls(r.data || []);
    } catch (_) { setCalls([]); }
    setLoading(false);
  }, [api, prospectId, i18n.language]);

  useEffect(() => { load(); }, [load]);

  const fmtDur = (s) => {
    s = s || 0;
    const m = Math.floor(s / 60), r = s % 60;
    return `${m}m ${r}s`;
  };

  if (loading) return null;
  if (!calls.length) return null;

  return (
    <div className="bg-[#0b0811] border border-white/5 rounded-xl p-3" data-testid="aircall-history">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1.5">
          <Sparkles className="w-3 h-3 text-emerald-400" />
          {t("crm.call_history")} · {calls.length}
        </div>
        <button onClick={load} className="text-slate-500 hover:text-white" title="Rafraichir">
          <RefreshCw className="w-3 h-3" />
        </button>
      </div>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {calls.map((c) => {
          const Icon = c.direction === "outbound" ? PhoneOutgoing : PhoneIncoming;
          const color = c.direction === "outbound" ? "text-emerald-300 bg-emerald-500/10" : "text-sky-300 bg-sky-500/10";
          return (
            <div key={c.call_log_id || c.aircall_call_id} className="bg-[#14101c] border border-white/5 rounded-lg p-2.5" data-testid={"aircall-call-" + (c.call_log_id || c.aircall_call_id)}>
              <div className="flex items-start gap-2">
                <div className={"w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 " + color}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[11px] text-slate-300">
                    <span className="font-bold uppercase">{c.direction === "outbound" ? t("prospects.outbound") : t("prospects.inbound")}</span>
                    <span className="text-slate-500">·</span>
                    <span>{fmtDur(c.duration)}</span>
                    <span className="text-slate-500">·</span>
                    <span className="text-slate-500 truncate">{c.phone || c.phone_to}</span>
                    <span className="text-slate-500 ml-auto">{(c.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  {(c.ai_summary_translated || c.ai_summary) && (
                    <div className="mt-1.5 bg-emerald-500/5 border border-emerald-500/15 rounded px-2 py-1.5">
                      <div className="text-[9px] uppercase tracking-wider text-emerald-400/80 font-bold mb-0.5 flex items-center gap-1">
                        <Sparkles className="w-2.5 h-2.5" /> {t("prospects.ai_summary")}
                        {c.ai_summary_translated && c.ai_summary_lang && (
                          <span className="ml-1 inline-flex items-center gap-0.5 text-[8px] px-1 py-0.5 rounded bg-emerald-500/20 normal-case">
                            <Languages className="w-2 h-2" /> {c.ai_summary_lang.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-200 leading-relaxed">{c.ai_summary_translated || c.ai_summary}</p>
                    </div>
                  )}
                  {c.recording_url && (
                    <a href={c.recording_url} target="_blank" rel="noreferrer"
                      className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-emerald-400 hover:underline">
                      <Play className="w-3 h-3" /> {t("prospects.recording")}
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
